import ReactGA from 'react-ga4';

// Check if we're in development mode
const isDevelopment = process.env.REACT_APP_NODE_ENV === 'development';
console.log('isDevelopment', isDevelopment);

// Only initialize GA in production
if (!isDevelopment && process.env.REACT_APP_GA_MEASUREMENT_ID) {
  console.log('Initializing GA in production mode');
  ReactGA.initialize(process.env.REACT_APP_GA_MEASUREMENT_ID);
}

// Initialize dataLayer array if it doesn't exist
if (!isDevelopment) {
  window.dataLayer = window.dataLayer || [];
}

// Helper function to push to dataLayer
const pushToDataLayer = (data) => {
  if (isDevelopment) {
    console.log('DataLayer push skipped in development:', data);
    return;
  }
  if (window.dataLayer) {
    window.dataLayer.push(data);
  }
};

// Meta Pixel helper functions
const logMetaEvent = (eventName, params = {}) => {
  if (isDevelopment) {
    console.log('Meta Pixel event skipped in development:', { eventName, params });
    return;
  }
  if (window.fbq) {
    // Use trackCustom for custom events, track for standard events
    const isStandardEvent = [
      'AddPaymentInfo', 'AddToCart', 'AddToWishlist', 'CompleteRegistration',
      'Contact', 'CustomizeProduct', 'Donate', 'FindLocation', 'InitiateCheckout',
      'Lead', 'PageView', 'Purchase', 'Schedule', 'Search', 'StartTrial',
      'SubmitApplication', 'Subscribe', 'ViewContent'
    ].includes(eventName);

    if (isStandardEvent) {
      window.fbq('track', eventName, params);
    } else {
      window.fbq('trackCustom', eventName, params);
    }
  }
};

// TikTok Pixel helper functions
const logTikTokEvent = (eventName, params = {}) => {
  if (isDevelopment) {
    console.log('TikTok Pixel event skipped in development:', { eventName, params });
    return;
  }
  if (window.ttq) {
    window.ttq.track(eventName, params);
  }
};

// Simple event logging
export const logEvent = (category, action, label, value = null) => {
  if (isDevelopment) {
    console.log('Analytics event skipped in development:', { category, action, label, value });
    return;
  }

  const eventParams = {
    category,
    action,
    label,
    ...(value && { value })
  };

  // Always send to Meta Pixel directly
  logMetaEvent(action, {
    category,
    label,
    ...(value && { value })
  });

  // If GTM is available, send through GTM
  if (window.dataLayer) {
    pushToDataLayer({
      event: 'customEvent',
      eventCategory: category,
      eventAction: action,
      eventLabel: label,
      ...(value && { eventValue: value })
    });
  } else {
    // Fallback to direct tracking if GTM is not available
    // Send to GA4
    ReactGA.event(eventParams);

    // Send to TikTok Pixel
    logTikTokEvent(action, {
      category,
      label,
      ...(value && { value })
    });
  }
};

// Simple page view logging
export const logPageView = (path) => {
  if (isDevelopment) {
    console.log('Analytics pageview skipped in development:', path);
    return;
  }

  // Send to GA4
  ReactGA.send({
    hitType: "pageview",
    page: path
  });

  // Send to GTM
  pushToDataLayer({
    event: 'virtualPageview',
    virtualPagePath: path
  });

  // Send to Meta Pixel
  if (window.fbq) {
    window.fbq('track', 'PageView');
  }

  // Send to TikTok Pixel
  if (window.ttq) {
    window.ttq.page();
  }
};

// Test analytics connections
export const testAnalytics = () => {
  if (isDevelopment) {
    console.log('Analytics testing skipped in development mode');
    return;
  }

  // Basic event
  logEvent('Test', 'Connection', 'Manual Test');
  
  // Basic pageview
  logPageView('/test');
  
  // Direct hit to GA
  if (process.env.REACT_APP_GA_MEASUREMENT_ID) {
    const pixel = new Image();
    pixel.src = `https://www.google-analytics.com/collect?v=1&t=event&tid=${process.env.REACT_APP_GA_MEASUREMENT_ID}&cid=555&ec=test&ea=pixel`;
  }
  
  // Test GTM
  pushToDataLayer({
    event: 'testEvent',
    testData: 'GTM Test'
  });

  // Test Meta Pixel
  if (window.fbq) {
    window.fbq('track', 'TestEvent');
  }

  // Test TikTok Pixel
  if (window.ttq) {
    window.ttq.track('TestEvent');
  }
}; 

// E-commerce event tracking
export const logAddToCart = (product) => {
  if (isDevelopment) {
    console.log('Add to cart event skipped in development:', product);
    return;
  }

  // Always send to Meta Pixel directly
  if (window.fbq) {
    window.fbq('track', 'AddToCart', {
      content_type: 'product',
      content_ids: [product.id],
      content_name: product.name,
      value: product.price,
      currency: 'USD',
      contents: [{
        id: product.id,
        quantity: product.quantity || 1
      }]
    });
  }

  // If GTM is available, send through GTM
  if (window.dataLayer) {
    // Clear previous ecommerce object
    pushToDataLayer({
      ecommerce: null
    });

    // Push add to cart event
    pushToDataLayer({
      event: 'add_to_cart',
      ecommerce: {
        currency: 'USD',
        value: product.price,
        items: [{
          item_id: product.id,
          item_name: product.name,
          price: product.price,
          quantity: product.quantity || 1,
          currency: 'USD',
          ...(product.category && { item_category: product.category }),
          ...(product.variant && { item_variant: product.variant })
        }]
      }
    });
  } else {
    // If GTM is not available, send to other platforms directly
    // Send to GA4
    ReactGA.event("add_to_cart", {
      currency: "USD",
      value: product.price,
      items: [{
        item_id: product.id,
        item_name: product.name,
        price: product.price,
        quantity: product.quantity || 1,
        ...(product.category && { item_category: product.category }),
        ...(product.variant && { item_variant: product.variant })
      }]
    });

    // Send to TikTok Pixel
    logTikTokEvent('AddToCart', {
      content_id: product.id,
      content_name: product.name,
      value: product.price,
      currency: 'USD',
      quantity: product.quantity || 1
    });
  }
};

// Begin Checkout Event Tracking
export const logBeginCheckout = ({ value, items }) => {
  if (isDevelopment) {
    console.log("Begin checkout event skipped in development:", {
      value,
      items,
    });
    return;
  }

  const normalizedItems = Array.isArray(items)
    ? items.filter((item) => item?.item_id && item?.item_name)
    : [];

  if (!normalizedItems.length) {
    console.log("Begin checkout event skipped: no valid items.");
    return;
  }

  pushToDataLayer({
    ecommerce: null,
  });

  pushToDataLayer({
    event: "begin_checkout",
    ecommerce: {
      currency: "USD",
      value: Number(value || 0),
      items: normalizedItems,
    },
  });
};

export const logPurchase = ({ transactionId, value, items }) => {
  if (isDevelopment) {
    console.log("Purchase event skipped in development:", {
      transactionId,
      value,
      items,
    });
    return;
  }

  if (!transactionId) {
    console.log("Purchase event skipped: missing transactionId.");
    return;
  }

  const normalizedItems = Array.isArray(items)
    ? items.filter((item) => item?.item_id && item?.item_name)
    : [];

  if (!normalizedItems.length) {
    console.log("Purchase event skipped: no valid items.");
    return;
  }

  const storageKey = `gtm_purchase_tracked_${transactionId}`;

  if (sessionStorage.getItem(storageKey) || localStorage.getItem(storageKey)) {
    console.log("Purchase event skipped: already tracked.", transactionId);
    return;
  }

  pushToDataLayer({
    ecommerce: null,
  });

  pushToDataLayer({
    event: "purchase",
    ecommerce: {
      transaction_id: String(transactionId),
      currency: "USD",
      value: Number(value || 0),
      items: normalizedItems,
    },
  });

  sessionStorage.setItem(storageKey, "true");
  localStorage.setItem(storageKey, "true");
};

// Signup funnel helpers
export const logSignupStart = (method = 'email') => {
  if (isDevelopment) {
    console.log('Signup start (dev):', { method });
    return;
  }

  // Only push to GTM dataLayer
  pushToDataLayer({
    event: 'sign_up_start',
    method: method
  });
};

export const logSignupSuccess = (method = 'email') => {
  if (isDevelopment) {
    console.log('Signup success (dev):', { method });
    return;
  }

  // Only push to GTM dataLayer
  pushToDataLayer({
    event: 'sign_up',
    method: method,
    status: 'success'
  });
};

export const logSignupFailure = (errorCode = 'unknown_error', method = 'email') => {
  if (isDevelopment) {
    console.log('Signup failure (dev):', { method, errorCode });
    return;
  }

  // Only push to GTM dataLayer
  pushToDataLayer({
    event: 'sign_up_error',
    method: method,
    error_code: errorCode
  });
}; 